export const LINE_AREA_MAX_RENDER_POINTS = 1200;
export const BAR_MAX_RENDER_POINTS = 400;
export const MIN_VIEWPORT_POINTS = 8;
// Above this many sectors a pie is unreadable and costs ~2 DOM nodes per slice,
// so the widget asks before drawing instead of rendering it outright.
export const PIE_MAX_RENDER_SLICES = 200;
export const LINE_AREA_SCROLL_ZOOM_POINT_THRESHOLD = LINE_AREA_MAX_RENDER_POINTS;
export const BAR_SCROLL_ZOOM_POINT_THRESHOLD = BAR_MAX_RENDER_POINTS;

export interface ChartViewport {
  endIndex: number;
  startIndex: number;
}

export type ChartDataPoint = { [key: string]: string | number };

export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

export const createChartViewport = (
  startIndex: number,
  windowSize: number,
  dataLength: number
): ChartViewport => {
  const safeWindowSize = clamp(windowSize, 1, dataLength);
  const safeStartIndex = clamp(startIndex, 0, dataLength - safeWindowSize);

  return {
    startIndex: safeStartIndex,
    endIndex: safeStartIndex + safeWindowSize - 1,
  };
};

export const getChartViewportSize = (viewport: ChartViewport): number => {
  return viewport.endIndex - viewport.startIndex + 1;
};

export const getVisibleChartData = <T extends ChartDataPoint>(
  data: T[],
  viewport: ChartViewport,
  enabled: boolean
): T[] => {
  return enabled ? data.slice(viewport.startIndex, viewport.endIndex + 1) : data;
};

const getNumericDataKeys = (data: ChartDataPoint[], xAxisDataKey?: string): string[] => {
  const keys = new Set<string>();

  data.forEach((entry: ChartDataPoint): void => {
    Object.entries(entry).forEach(([key, value]: [string, string | number]): void => {
      if (key !== xAxisDataKey && typeof value === 'number' && Number.isFinite(value)) {
        keys.add(key);
      }
    });
  });

  return Array.from(keys);
};

const addEvenlySampledIndexes = (
  includedIndexes: Set<number>,
  firstInteriorIndex: number,
  lastInteriorIndex: number,
  bucketCount: number
): void => {
  const interiorLength = lastInteriorIndex - firstInteriorIndex + 1;

  for (let bucketIndex = 0; bucketIndex < bucketCount; bucketIndex += 1) {
    const bucketStart =
      firstInteriorIndex + Math.floor((bucketIndex * interiorLength) / bucketCount);
    const bucketEnd =
      firstInteriorIndex + Math.floor(((bucketIndex + 1) * interiorLength) / bucketCount) - 1;
    const sampleIndex = Math.round((bucketStart + bucketEnd) / 2);

    includedIndexes.add(sampleIndex);
  }
};

const addBucketExtremaIndexes = (
  data: ChartDataPoint[],
  includedIndexes: Set<number>,
  numericDataKeys: string[],
  firstInteriorIndex: number,
  lastInteriorIndex: number,
  bucketCount: number
): void => {
  const interiorLength = lastInteriorIndex - firstInteriorIndex + 1;

  for (let bucketIndex = 0; bucketIndex < bucketCount; bucketIndex += 1) {
    const bucketStart =
      firstInteriorIndex + Math.floor((bucketIndex * interiorLength) / bucketCount);
    const bucketEnd =
      firstInteriorIndex + Math.floor(((bucketIndex + 1) * interiorLength) / bucketCount) - 1;

    numericDataKeys.forEach((key: string): void => {
      let minIndex: number | null = null;
      let maxIndex: number | null = null;
      let minValue = Number.POSITIVE_INFINITY;
      let maxValue = Number.NEGATIVE_INFINITY;

      for (let index = bucketStart; index <= bucketEnd; index += 1) {
        const value = data[index][key];

        if (typeof value !== 'number' || !Number.isFinite(value)) {
          continue;
        }

        if (value < minValue) {
          minValue = value;
          minIndex = index;
        }

        if (value > maxValue) {
          maxValue = value;
          maxIndex = index;
        }
      }

      if (minIndex !== null) {
        includedIndexes.add(minIndex);
      }

      if (maxIndex !== null) {
        includedIndexes.add(maxIndex);
      }
    });
  }
};

export const decimateChartData = <T extends ChartDataPoint>(
  data: T[],
  maxPoints: number,
  xAxisDataKey?: string
): T[] => {
  if (data.length <= maxPoints) {
    return data;
  }

  if (maxPoints <= 0) {
    return [];
  }

  if (maxPoints === 1) {
    return [data[0]];
  }

  if (maxPoints === 2) {
    return [data[0], data[data.length - 1]];
  }

  const includedIndexes = new Set<number>([0, data.length - 1]);
  const firstInteriorIndex = 1;
  const lastInteriorIndex = data.length - 2;
  const numericDataKeys = getNumericDataKeys(data, xAxisDataKey);

  if (lastInteriorIndex < firstInteriorIndex) {
    return [data[0], data[data.length - 1]];
  }

  if (numericDataKeys.length === 0) {
    addEvenlySampledIndexes(includedIndexes, firstInteriorIndex, lastInteriorIndex, maxPoints - 2);
  } else {
    const indexesPerBucket = numericDataKeys.length * 2;
    const bucketCount = Math.max(1, Math.floor((maxPoints - 2) / indexesPerBucket));

    addBucketExtremaIndexes(
      data,
      includedIndexes,
      numericDataKeys,
      firstInteriorIndex,
      lastInteriorIndex,
      bucketCount
    );
  }

  const sortedIndexes = Array.from(includedIndexes)
    .sort((firstIndex: number, secondIndex: number): number => firstIndex - secondIndex)
    .slice(0, maxPoints);

  if (sortedIndexes[sortedIndexes.length - 1] !== data.length - 1) {
    sortedIndexes[sortedIndexes.length - 1] = data.length - 1;
  }

  return sortedIndexes.map((index: number): T => data[index]);
};
